import React, { useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { confirmDialog } from './ConfirmDialog';
import { groupCarriersByName } from '../utils/carrierHelpers';

function StateCard({ 
  stateCode, 
  carriers, 
  allCarriers, 
  onUpdate, 
  onDelete, 
  isEditing, 
  setIsEditing, 
  isAdminView = true,
  readOnly = false
}) {
  const [expanded, setExpanded] = useState(false);
  const [editCarriers, setEditCarriers] = useState(carriers);
  const [carrierSearchTerm, setCarrierSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const groupedCarriers = groupCarriersByName(carriers);
  
  // Check if "Select All Carriers" special carrier exists (CarrierId: 424242424)
  const hasSelectAllCarrier = allCarriers.some(c => c.CarrierId === 424242424);

  // ── State-name filter ────────────────────────────────────────────────────────
  // If a carrier name contains another US state name (e.g. "Ambetter of Oklahoma"
  // when editing Alabama), exclude it — it's not relevant to the current state.
  const US_STATE_NAMES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
    'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
    'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
    'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
    'Virginia','Washington','West Virginia','Wisconsin','Wyoming'
  ];
  // Full name of the current state being edited (from 2-letter code)
  const STATE_CODE_TO_NAME = {
    AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
    CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',
    HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',
    KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',
    MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',
    NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',
    NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
    OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
    SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',
    VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming'
  };
  const currentStateName = STATE_CODE_TO_NAME[stateCode] || '';

  function carrierBelongsToState(carrierName) {
    if (!carrierName) return true;
    const name = carrierName.toLowerCase();
    for (const stateName of US_STATE_NAMES) {
      // Skip the current state — carriers with its name are fine
      if (stateName === currentStateName) continue;
      if (name.includes(stateName.toLowerCase())) return false;
    }
    return true;
  }

  // Group carrier options by name for dropdown and filter out already selected + other-state carriers
  const groupedAllCarriers = groupCarriersByName(allCarriers);
  const carrierOptions = groupedAllCarriers
    .filter(group => {
      // Filter out carriers that belong to a different state
      if (!carrierBelongsToState(group.name)) return false;
      const allSelected = group.carrierIds.every(carrierId =>
        editCarriers.some(ec => ec.CarrierId === carrierId)
      );
      return !allSelected;
    })
    .map(group => ({
      value: group,
      label: isAdminView ? `${group.name} (${group.carrierIds.length})` : group.name
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(stateCode, editCarriers);
      setIsEditing(false);
    } catch (error) {
      toast.error('Error saving state carriers');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditCarriers(carriers);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    const confirmed = await confirmDialog(`Remove ${stateCode} and all its carriers?`);
    if (confirmed) {
      setDeleting(true);
      try {
        await onDelete(stateCode);
      } catch (error) {
        toast.error('Error deleting state');
      } finally {
        setDeleting(false);
      }
    }
  };

  const filteredCarriers = carrierSearchTerm
    ? carrierOptions.filter(option =>
        option.label.toLowerCase().includes(carrierSearchTerm.toLowerCase())
      )
    : carrierOptions;

  return (
    <div className="state-card">
      <div className="state-card-header">
        <div className="state-card-title" onClick={() => !isEditing && setExpanded(!expanded)}>
          <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>
            {expanded ? '▼' : '▶'}
          </span>
          <h4>{stateCode}</h4>
          <span className="state-carrier-count">
            {groupedCarriers.length} carrier{groupedCarriers.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="state-card-actions">
          {readOnly ? (
            // Read-only mode: no buttons
            null
          ) : !isEditing ? (
            // View mode: show Edit and Delete buttons
            <>
              <button 
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                  setExpanded(true);
                }}
                disabled={deleting}
              >
                ✏️ Edit
              </button>
              {isAdminView && (
                <button 
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  disabled={deleting || saving}
                >
                  {deleting ? '🗑️ Deleting...' : '🗑️ Delete'}
                </button>
              )}
            </>
          ) : (
            // Edit mode: show Save and Cancel buttons
            <>
              <button 
                type="button"
                className="btn btn-sm btn-success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                disabled={saving}
              >
                {saving ? '✓ Saving...' : '✓ Save'}
              </button>
              <button 
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                disabled={saving}
              >
                ✕ Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="state-card-content">
          {!isEditing ? (
            <div className="grouped-carrier-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {groupedCarriers.map((group, idx) => (
                <div key={idx} className="grouped-carrier-item" style={{ 
                  padding: '8px 12px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span className="carrier-name" style={{ 
                    flex: 1,
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    {group.name}
                  </span>
                  {isAdminView && (
                    <span className="carrier-plan-count" style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontWeight: '600'
                    }}>
                      ({group.carrierIds.length})
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="state-card-edit-form">
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  {filteredCarriers.length} {carrierSearchTerm ? 'filtered' : 'total'} carrier{filteredCarriers.length !== 1 ? 's' : ''}
                </span>
                {filteredCarriers.length > 0 && !hasSelectAllCarrier && (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const newCarriers = [...editCarriers];
                      filteredCarriers.forEach(groupOption => {
                        groupOption.value.carrierIds.forEach(carrierId => {
                          const carrier = allCarriers.find(c => c.CarrierId === carrierId);
                          if (carrier && !newCarriers.some(nc => nc.CarrierId === carrierId)) {
                            newCarriers.push(carrier);
                          }
                        });
                      });
                      setEditCarriers(newCarriers);
                      setCarrierSearchTerm('');
                    }}
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    + Add All {carrierSearchTerm ? 'Search Results' : 'Carriers'}
                  </button>
                )}
              </div>
              <Select
                isMulti
                options={carrierOptions}
                value={groupCarriersByName(editCarriers).map(group => ({
                  value: group,
                  label: isAdminView ? `${group.name} (${group.carrierIds.length})` : group.name
                }))}
                onChange={(selected) => {
                  if (!selected || selected.length === 0) {
                    setEditCarriers([]);
                  } else {
                    const newCarriers = [];
                    selected.forEach(item => {
                      item.value.carrierIds.forEach(carrierId => {
                        const carrier = allCarriers.find(c => c.CarrierId === carrierId);
                        if (carrier) {
                          newCarriers.push(carrier);
                        }
                      });
                    });
                    setEditCarriers(newCarriers);
                  }
                  setCarrierSearchTerm('');
                }}
                onInputChange={(value, action) => {
                  if (action.action === 'input-change') {
                    setCarrierSearchTerm(value);
                  }
                }}
                inputValue={carrierSearchTerm}
                placeholder="Search and select carriers..."
                filterOption={(option, inputValue) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StateCard;
